import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { devicePayload } from "../lib/device";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import analytics from "../lib/analytics";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// The Emergent OAuth flow redirects back to this page with `#session_id=...` in the
// URL fragment. We POST it once to the backend, store the issued JWT, then go to /app.

export default function AuthCallback() {
  const nav = useNavigate();
  const { adoptSession } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const session_id = m ? decodeURIComponent(m[1]) : null;
    if (!session_id) {
      // Sometimes Emergent returns the param in the querystring instead
      const qp = new URLSearchParams(window.location.search).get("session_id");
      if (!qp) {
        setError("No session_id from auth provider.");
        return;
      }
    }
    const sid =
      session_id ||
      new URLSearchParams(window.location.search).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {
          session_id: sid,
          device: devicePayload(),
        });
        adoptSession(data.token, data.user);
        analytics.login("Google");
        toast.success(`Welcome, ${data.user.full_name?.split(" ")[0] || "clinician"}`);
        // Clean the hash so reloads don't replay.
        window.history.replaceState({}, "", "/auth/callback");
        // Google sign-ups have no HPCSA yet — route them to capture it before
        // they can use the app.
        if (!data.user.hpcsa_number) {
          nav("/complete-profile", { replace: true });
        } else {
          nav("/app/dashboard", { replace: true });
        }
      } catch (err) {
        const msg = err.response?.data?.detail || "Google sign-in failed";
        setError(msg);
        toast.error(msg);
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
      data-testid="auth-callback-page"
    >
      <div className="text-center max-w-sm">
        {!error ? (
          <>
            <Loader2 className="w-10 h-10 text-[#111827] animate-spin mx-auto" />
            <h1 className="font-heading text-xl font-bold text-[#111827] mt-4">
              Signing you in…
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Securely verifying your Google account.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-heading text-xl font-bold text-[#111827]">
              Sign-in problem
            </h1>
            <p className="text-sm text-slate-600 mt-2">{error}</p>
            <button
              onClick={() => nav("/login")}
              className="mt-5 inline-block bg-[#0B5FFF] text-white px-4 py-2 rounded-md text-sm"
              data-testid="auth-callback-back-login"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
